import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { MessageIcon } from '../../../components/shared-components/message-icon';
import { UserContext } from '../../../contexts/userContext';
import MessageAPI from '../../../axios/message';
import { formatNumber, PRICE_UNITS } from '../../main-page/home-utils';
import { PhoneIcon } from '../../../components/shared-components/listing-icons';

const BOOKING_DAYS = 6;
const TIMES = ['10:00', '12:00', '15:00', '17:00', '18:30'];

const initials = (name) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('');

export const ContactPanel = ({ listing, onMessage }) => {
    const { user } = useContext(UserContext);
    const { t, i18n } = useTranslation(["details", "home", "listing"]);
    const navigate = useNavigate();
    const [phoneShown, setPhoneShown] = useState(false);
    const [day, setDay] = useState(null);
    const [time, setTime] = useState(null);
    // null, 'sending', 'sent' or 'error'
    const [booking, setBooking] = useState(null);

    const locale = i18n.language === 'en' ? 'en-GB' : 'sr-Latn-RS';
    const price = parseFloat(listing.price) || 0;
    const area = parseFloat(listing.area) || 0;
    const typeIndex = (listing.listing_type?.id || 1) - 1;
    const priceSuffix = typeIndex > 0 ? ` ${t(PRICE_UNITS[typeIndex], { ns: 'home' })}` : '';
    const phones = [listing.phone, listing.mobile].filter(Boolean).filter((p, i, all) => all.indexOf(p) === i);
    const bills = parseFloat(listing.bills) || 0;

    // the next days, starting tomorrow
    const days = useMemo(() => Array.from({ length: BOOKING_DAYS }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() + i + 1);
        return date;
    }), []);

    const canBook = day !== null && time !== null && booking !== 'sending';
    const slotLabel = day !== null && time !== null
        ? `${days[day].toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}, ${TIMES[time]}`
        : '';

    // there is no booking system, so a viewing request goes to the publisher as a message
    const book = () => {
        if (!canBook) return;
        if (!user) {
            navigate('/login');
            return;
        }
        setBooking('sending');
        MessageAPI.sendMessage({
            subject: t('viewing_subject', { title: listing.title }),
            body: t('viewing_body', { slot: slotLabel }),
            reply_to: null,
            listing: listing.id,
        }).then(() => setBooking('sent')).catch(() => setBooking('error'));
    };

    return (
        <aside className='hs-det-aside'>
            <div className='hs-contact'>
                <div>
                    <div className='hs-contact-price-row'>
                        <span className='hs-contact-price'>{price > 0 ? `${formatNumber(price)} €${priceSuffix}` : t('price_placeholder', { ns: 'listing' })}</span>
                        {typeIndex === 0 && price > 0 && area > 0 && <span className='hs-contact-ppm'>{formatNumber(price / area)} €/m²</span>}
                    </div>
                    {bills > 0 && <p className='hs-contact-note'>{t('bills_note', { amount: formatNumber(bills) })}</p>}
                </div>
                <div className='hs-contact-publisher'>
                    <span className='hs-contact-avatar'>{initials(listing.user)}</span>
                    <span className='hs-contact-publisher-text'>
                        <span className='hs-contact-publisher-name'>{listing.user}</span>
                        <span className='hs-contact-publisher-type'>{listing.user_type?.label}</span>
                    </span>
                </div>
                <div className='hs-contact-actions'>
                    {phones.length > 0 && (phoneShown
                        ? phones.map(phone => (
                            <a key={phone} className='hs-contact-call' href={`tel:${phone}`}><PhoneIcon />{phone}</a>
                        ))
                        : <button className='hs-contact-call' onClick={() => setPhoneShown(true)}><PhoneIcon />{t('show_phone')}</button>)}
                    <button className='hs-contact-msg' onClick={onMessage}>
                        <MessageIcon />
                        {t('send_message')}
                    </button>
                </div>
                <div className='hs-booking'>
                    {booking === 'sent' ? <>
                        <div className='hs-booking-done'>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
                            <span><strong>{t('viewing_sent_title')}</strong><br />{t('viewing_sent_text', { slot: slotLabel })}</span>
                        </div>
                        <button className='hs-booking-change' onClick={() => setBooking(null)}>{t('viewing_change')}</button>
                    </> : <>
                        <span className='hs-booking-title'>{t('viewing_title')}</span>
                        <div className='hs-booking-days'>
                            {days.map((date, i) => (
                                <button key={date.toDateString()} className={i === day ? 'on' : ''} onClick={() => setDay(i)}>
                                    <span>{date.toLocaleDateString(locale, { weekday: 'short' })}</span>
                                    <strong>{date.getDate()}</strong>
                                </button>
                            ))}
                        </div>
                        <div className='hs-booking-times'>
                            {TIMES.map((label, i) => (
                                <button key={label} className={i === time ? 'on' : ''} onClick={() => setTime(i)}>{label}</button>
                            ))}
                        </div>
                        {booking === 'error' && <p className='hs-booking-error'>{t('viewing_error')}</p>}
                        <button className={`hs-booking-submit ${canBook ? 'ready' : ''}`} disabled={!canBook} onClick={book}>{t('viewing_submit')}</button>
                    </>}
                </div>
            </div>
            <div className='hs-contact-meta'>
                <span>{t('listing_no', { id: listing.id })} · {t('published_on', { date: new Date(listing.date_created).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) })}</span>
            </div>
        </aside>
    );
};
